// «الصيانة» — the network KPI cards. Backlog cards (open / overdue / awaiting) are as of now;
// the flow cards are for the chosen period. Numbers come from GET /maintenance/overview.
import { AlarmClock, CheckCircle2, ClipboardList, Clock3, Coins, Hourglass, Inbox, Repeat, Store, Timer, Wrench, Zap } from "lucide-react";
import { MAINT } from "@/constants/testIds";
import { fmtDuration, fmtInt, fmtPct, fmtSar, fmtTemplate } from "./maintText";

function Kpi({ id, icon: Icon, label, value, sub, hint, tone, onClick, testId }) {
    const toneCls = tone === "danger"
        ? "border-[color:var(--danger)]/40 bg-[color:var(--danger-soft)]"
        : tone === "warning"
            ? "border-[color:var(--warning)]/40 bg-[color:var(--warning-soft)]"
            : "border-[color:var(--border-soft)] bg-[color:var(--bg-surface)]";
    const Tag = onClick ? "button" : "div";
    return (
        <Tag type={onClick ? "button" : undefined} onClick={onClick} data-testid={testId(id)} title={hint || undefined}
             className={`min-w-0 rounded-[var(--radius-md)] border px-3 py-2.5 text-start ${toneCls} ${onClick ? "transition-[background-color] duration-150 hover:bg-[color:var(--brand-orange-soft)]" : ""}`}>
            <span className="flex items-center gap-1.5 text-[11px] font-bold leading-tight text-[color:var(--ink-muted)]">
                <Icon className="h-3.5 w-3.5 shrink-0 text-[color:var(--brand-brown)]" />
                <span className="truncate">{label}</span>
            </span>
            <span className="mt-1 block truncate text-lg font-extrabold tnum text-[color:var(--ink)]">{value}</span>
            {sub ? <span className="block truncate text-[10px] font-semibold text-[color:var(--ink-faint)]">{sub}</span> : null}
        </Tag>
    );
}

export function MaintKpis({ network, t, lang, onPick, compact = false }) {
    const n = network || {};
    const tid = compact ? MAINT.branchKpi : MAINT.kpi;     // the branch sheet's cards have their own ids
    const met = n.met_target || {};
    const ttc = n.time_to_complete || {};
    const fr = n.first_response || {};
    const metSub = (met.met || met.missed)
        ? [fmtTemplate(t("mt_met_of"), { met: met.met, n: met.met + met.missed }),
            met.computed ? fmtTemplate(t("mt_computed_n"), { n: met.computed }) : null].filter(Boolean).join(" · ")
        : null;
    // the value is the average; the median sits under it (a few long jobs pull the average up)
    const durSub = (s) => (s.n ? `${t("mt_avg")} · ${t("mt_median")} ${fmtDuration(s.median_minutes, t)}` : null);
    return (
        <section data-testid={compact ? undefined : MAINT.kpis}
                 className={`grid grid-cols-2 gap-2 sm:grid-cols-3 ${compact ? "" : "lg:grid-cols-4 xl:grid-cols-6"}`}>
            <Kpi testId={tid} id="open_requests" icon={Inbox} label={t("mt_kpi_open_requests")} value={fmtInt(n.open_requests, lang)}
                 onClick={onPick ? () => onPick({ type: "wr", status: "open" }) : undefined} />
            <Kpi testId={tid} id="open_work_orders" icon={Wrench} label={t("mt_kpi_open_work_orders")} value={fmtInt(n.open_work_orders, lang)}
                 onClick={onPick ? () => onPick({ type: "wo", status: "open" }) : undefined} />
            <Kpi testId={tid} id="overdue" icon={AlarmClock} label={t("mt_kpi_overdue")} value={fmtInt(n.overdue, lang)}
                 tone={n.overdue ? "danger" : undefined}
                 onClick={onPick ? () => onPick({ type: "wo", status: "open", overdue: true }) : undefined} />
            <Kpi testId={tid} id="awaiting_close" icon={Hourglass} label={t("mt_kpi_awaiting_close")} value={fmtInt(n.awaiting_close, lang)}
                 onClick={onPick ? () => onPick({ type: "wo", status: "awaiting_close" }) : undefined} />
            <Kpi testId={tid} id="awaiting_branch" icon={Store} label={t("mt_kpi_awaiting_branch")}
                 value={fmtInt(n.awaiting_branch_confirmation, lang)} tone={n.awaiting_branch_confirmation ? "warning" : undefined}
                 onClick={onPick ? () => onPick({ type: "wo", status: "awaiting_branch" }) : undefined} />
            <Kpi testId={tid} id="met_target" icon={CheckCircle2} label={t("mt_kpi_met_target")} value={fmtPct(met.pct, lang)} sub={metSub} />
            <Kpi testId={tid} id="ttc" icon={Timer} label={t("mt_kpi_ttc")} value={fmtDuration(ttc.avg_minutes, t)} sub={durSub(ttc)}
                 hint={t("mt_ttc_hint")} />
            <Kpi testId={tid} id="first_response" icon={Zap} label={t("mt_kpi_first_response")} value={fmtDuration(fr.avg_minutes, t)}
                 sub={durSub(fr)} hint={t("mt_fr_hint")} />
            <Kpi testId={tid} id="repeat" icon={Repeat} label={t("mt_kpi_repeat")} value={fmtInt(n.repeat_failures, lang)} sub={t("mt_repeat_hint")}
                 hint={t("mt_repeat_hint")} />
            <Kpi testId={tid} id="parts_cost" icon={Coins} label={t("mt_kpi_parts_cost")} value={fmtSar(n.parts_cost, lang, t)} />
            <Kpi testId={tid} id="requests_created" icon={ClipboardList} label={t("mt_kpi_requests_created")} value={fmtInt(n.requests_created, lang)} />
            <Kpi testId={tid} id="completed" icon={Clock3} label={t("mt_kpi_completed")} value={fmtInt(n.work_orders_completed, lang)} />
        </section>
    );
}
